import React, { useMemo, useState } from 'react';
import { alpha, Box, Button, Popover, Stack } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import { getDatePickerProviderProps } from '../../../../utils/datePickerLocale';
import { MIS_UI } from '../../utils/misConstants';

const toDayjs = (value) => (value ? dayjs(value) : null);

const PRESET_KEYS = ['this_month', 'quarterly', 'last_6_months', 'one_year', 'custom'];

const resolvePresetDates = (preset) => {
  const today = dayjs();
  switch (preset) {
    case 'this_month':
      return { startDate: today.startOf('month'), endDate: today };
    case 'quarterly': {
      const quarterStartMonth = Math.floor(today.month() / 3) * 3;
      return { startDate: today.month(quarterStartMonth).startOf('month'), endDate: today };
    }
    case 'last_6_months':
      return { startDate: today.subtract(6, 'month'), endDate: today };
    case 'one_year':
      return { startDate: today.subtract(1, 'year'), endDate: today };
    default:
      return null;
  }
};

const datesMatchPreset = (preset, startDate, endDate) => {
  if (!preset || preset === 'custom' || !startDate || !endDate) {
    return false;
  }
  const resolved = resolvePresetDates(preset);
  if (!resolved) {
    return false;
  }
  return (
    resolved.startDate.format('YYYY-MM-DD') === dayjs(startDate).format('YYYY-MM-DD') &&
    resolved.endDate.format('YYYY-MM-DD') === dayjs(endDate).format('YYYY-MM-DD')
  );
};

const detectPreset = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');
  for (const key of PRESET_KEYS.filter((item) => item !== 'custom')) {
    const resolved = resolvePresetDates(key);
    if (
      resolved &&
      resolved.startDate.format('YYYY-MM-DD') === start &&
      resolved.endDate.format('YYYY-MM-DD') === end
    ) {
      return key;
    }
  }
  return 'custom';
};

const resolveActivePreset = (periodPreset, startDate, endDate) => {
  if (periodPreset && periodPreset !== 'custom' && datesMatchPreset(periodPreset, startDate, endDate)) {
    return periodPreset;
  }
  if (periodPreset === 'custom') {
    return 'custom';
  }
  return detectPreset(startDate, endDate);
};

const InvoicingPeriodFilter = ({ startDate, endDate, periodPreset, onChange, disabled = false }) => {
  const { misDashboard: m, language } = useTranslatedMessages();
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftPreset, setDraftPreset] = useState('');
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const open = Boolean(anchorEl);
  const datePickerLocale = getDatePickerProviderProps(language);

  const activePreset = useMemo(
    () => resolveActivePreset(periodPreset, startDate, endDate),
    [periodPreset, startDate, endDate],
  );

  const emitChange = (nextStart, nextEnd, nextPreset) => {
    onChange({
      startDate: nextStart ? dayjs(nextStart).format('YYYY-MM-DD') : null,
      endDate: nextEnd ? dayjs(nextEnd).format('YYYY-MM-DD') : null,
      periodPreset: nextPreset,
    });
  };

  const openPopover = (event) => {
    setDraftPreset(activePreset || '');
    setDraftStart(toDayjs(startDate));
    setDraftEnd(toDayjs(endDate));
    setAnchorEl(event.currentTarget);
  };

  const applyPreset = (preset) => {
    setDraftPreset(preset);
    if (preset === 'custom') {
      return;
    }
    const resolved = resolvePresetDates(preset);
    if (!resolved) {
      return;
    }
    emitChange(resolved.startDate, resolved.endDate, preset);
    setAnchorEl(null);
  };

  const applyCustom = () => {
    if (!draftStart || !draftEnd) {
      return;
    }
    emitChange(draftStart, draftEnd, 'custom');
    setAnchorEl(null);
  };

  const clearCustom = () => {
    setDraftStart(null);
    setDraftEnd(null);
  };

  const presetLabel = (key) => {
    const labels = m.filters?.presets ?? {};
    return (
      {
        this_month: labels.this_month ?? 'This Month',
        quarterly: labels.quarterly ?? 'Quarterly',
        last_6_months: labels.last_6_months ?? 'Last 6 Months',
        one_year: labels.one_year ?? '1 Year',
        custom: labels.custom ?? 'Custom',
      }[key] ?? key
    );
  };

  const buttonLabel = (() => {
    if (activePreset && activePreset !== 'custom') {
      return presetLabel(activePreset);
    }
    if (startDate && endDate) {
      return `${dayjs(startDate).format('DD/MM/YY')} - ${dayjs(endDate).format('DD/MM/YY')}`;
    }
    if (activePreset === 'custom') {
      return m.filters?.custom_range ?? 'Custom Range';
    }
    return m.filters?.invoicing_period ?? 'Invoicing Period';
  })();

  const rangeSummary = (() => {
    if (draftStart && draftEnd) {
      return `${draftStart.format('MMM D')} - ${draftEnd.format('MMM D')}`;
    }
    if (draftStart) {
      return draftStart.format('MMM D');
    }
    return m.filters?.select_dates ?? 'Select dates';
  })();

  return (
    <>
      <Button
        type="button"
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={openPopover}
        startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
        sx={{
          textTransform: 'none',
          height: 32,
          px: 1.5,
          minWidth: 200,
          justifyContent: 'space-between',
          fontSize: 12,
          fontWeight: 400,
          borderColor: MIS_UI.BORDER,
          bgcolor: 'transparent',
          color: MIS_UI.TEXT,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'inherit' },
          '&:hover': {
            borderColor: MIS_UI.BORDER,
            bgcolor: MIS_UI.SURFACE_HOVER,
            color: MIS_UI.TEXT_HOVER,
          },
        }}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, overflow: 'hidden' } } }}
      >
        <Stack direction="row">
          <Box sx={{ p: 0.5, minWidth: 160 }}>
            {PRESET_KEYS.map((key) => (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => applyPreset(key)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: 14,
                  fontWeight: draftPreset === key ? 600 : 400,
                  color: draftPreset === key ? 'primary.main' : 'text.primary',
                  bgcolor: draftPreset === key ? alpha('#4f5efa', 0.1) : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {presetLabel(key)}
              </Box>
            ))}
          </Box>

          {draftPreset === 'custom' && (
            <Box sx={{ p: 1.5, borderLeft: 1, borderColor: 'divider', minWidth: 280 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  mb: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: draftStart ? alpha('#4f5efa', 0.3) : 'divider',
                  bgcolor: draftStart ? alpha('#4f5efa', 0.05) : 'action.hover',
                  textAlign: 'center',
                  fontSize: 14,
                  color: draftStart ? 'text.primary' : 'text.secondary',
                }}
              >
                {rangeSummary}
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs} {...datePickerLocale}>
                <DateCalendar
                  value={draftEnd || draftStart}
                  onChange={(date) => {
                    if (!draftStart || (draftStart && draftEnd)) {
                      setDraftStart(date);
                      setDraftEnd(null);
                      return;
                    }
                    if (date.isBefore(draftStart, 'day')) {
                      setDraftStart(date);
                      setDraftEnd(null);
                      return;
                    }
                    setDraftEnd(date);
                  }}
                  sx={{ width: '100%', maxHeight: 300 }}
                />
              </LocalizationProvider>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={clearCustom}
                  sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
                >
                  {m.filters?.clear ?? 'Clear'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!draftStart || !draftEnd}
                  onClick={applyCustom}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  {m.filters?.apply ?? 'Apply'}
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default InvoicingPeriodFilter;
